let globalNavigate: any = null;
let pendingPath: string | null = null;

export const setNavigate = (navigateFn) => {
  globalNavigate = navigateFn;
  // Flush a navigation that was requested before the Router was ready.
  if (globalNavigate && pendingPath != null) {
    const path = pendingPath;
    pendingPath = null;
    globalNavigate(path);
  }
};

export const _navigate = (path) => {
  if (globalNavigate) {
    globalNavigate(
      path
    );
  } else {
    // Router not mounted yet — queue the navigation; setNavigate() flushes it.
    pendingPath = path;
  }
};

/**
 * Resolve the site ids belonging to a member from the loaded `entities`.
 *
 * Uses the UNION of:
 *  - sites whose own `memberId` matches (the common/admin case), and
 *  - the member's `changelog` site ids (covers end-user app accounts that share
 *    a site whose "primary" `memberId` points to a different member).
 *
 * For a normal member both sources agree, so this is a safe superset with no
 * regression for admin/supervisor; for shared end-user accounts it recovers the
 * sites that `site.memberId` filtering would otherwise miss.
 *
 * CM-5124: the changelog is NOT scoped to the caller's role — the catalog builds
 * it from every SiteUser row of the member, so for a supervisor it also lists
 * sites of communities they don't supervise (a member who moved, or who simply
 * owns sites in more than one community). `entities.site` IS role-scoped, so we
 * keep only changelog ids that appear there. Without this the member page sends
 * out-of-scope ids to `POST site/{kind}/grouped`, which denies the WHOLE batch
 * with 403 ERR_AUTH_004 and leaves every widget empty.
 */
export function getMemberSiteIds(metadata: any, memberId: string): string[] {
  const entities = metadata?.entities || {};
  const sites = entities.site || [];
  const byMember = sites
    .filter((s: any) => s?.memberId === memberId)
    .map((s: any) => s?.siteId);
  const members = entities.member || entities.members || [];
  const member = members.find((m: any) => m?.memberId === memberId);
  // Only sites visible in `entities` — i.e. inside the caller's scope.
  const inScope = new Set(sites.map((s: any) => s?.siteId).filter(Boolean));
  const byChangelog = ((member?.changelog as any[]) || [])
    .map((c: any) => c?.siteId)
    .filter((siteId: string) => inScope.has(siteId));
  return Array.from(new Set([...byMember, ...byChangelog].filter(Boolean)));
}